import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SupportChatStateCountForm } from '@common/generated-api/models/support-chat-state-count-form';
import { AdminNotificationsApiService } from '@common/generated-api/services/admin-notifications-api.service';
import { AdminProposalApiService } from '@common/generated-api/services/admin-proposal-api.service';
import { AdminSupportApiService } from '@common/generated-api/services/admin-support-api.service';
import { ActivityService } from '@shared/services/activity.service';
import { AppModes, AppService } from '@shared/services/app.service';
import { AuthService } from '@shared/services/auth.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { IsArraysEqual } from '@shared/utils/array.utils';
import { CommonQueryParams } from '@shared/utils/constants';
import { environment } from 'projects/admin/src/environments/environment';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, startWith, tap, throttleTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CountersService {
  pollingTimer: any;
  basePollingTimeout = 60; // in seconds, 60
  inactivityCheckingTimeout = 60; // in seconds, 60
  inactivityTimeout = 20 * 60; // in seconds, 20 * 60
  initialized = false;
  inactive = false;
  activitySubject = new Subject();
  lastCoutersValue: any[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private activityService: ActivityService,
    private sidebarService: SidebarService,
    private zone: NgZone,
    private adminSupportApiService: AdminSupportApiService,
    private adminProposalApiService: AdminProposalApiService,
    private adminNotificationsApiService: AdminNotificationsApiService,
    private authService: AuthService,
    private appService: AppService
  ) {
  }

  init() {
    if (this.initialized) {
      // eslint-disable-next-line no-console
      console.error('CountersService has been initialized already');
      return;
    }
    this.initialized = true;

    setTimeout(() => {
      this.startPolling();
    },0)

    // check inactivity
    this.activitySubject.pipe(startWith(true), debounceTime(this.inactivityTimeout * 1000)).subscribe({
      next: (data) => {
        this.inactive = true;
      },
    });

    this.activityService.click$
      .pipe(throttleTime(this.inactivityCheckingTimeout * 1000))
      .subscribe({
        next: (data) => {
          if (this.inactive) {
            this.inactive = false;
            this.startPolling();
            this.activitySubject.next(true);
          } else {
            this.activitySubject.next();
          }
        },
      });
  }

  private startPolling() {
    this.zone.runOutsideAngular(() => {
      this.pollCounters();
    });
  }

  private pollCounters() {
    // console.log('Polling counters', new Date(), this.inactive);
    if (this.inactive) {
      return;
    }
    if (!this.authService.getAuthData().isLoggedIn) {
      return;
    }
    forkJoin([ this.getRequestsCount(), this.getProposalsCount(), this.getNotificationsCount() ]).subscribe({
      next: (data) => {
        if (IsArraysEqual(data, this.lastCoutersValue)) {
          // console.log('Counters not changed');
        } else {
          this.lastCoutersValue = data;
          this.zone.run(() => {
            setTimeout(() => {
              // trigger change detection
            }, 0);
          });
        }
        if (environment.production) {
          // temporary disabled in dev mod - todocheckprod - enable
          this.setTimer();
        }
      },
    });
  }

  private setTimer(timeout?: number) {
    this.removeTimer();
    this.pollingTimer = setTimeout(() => {
      this.pollCounters();
    }, (timeout || this.basePollingTimeout) * 1000);
  }

  private removeTimer() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
    }
  }

  forceUpdateRequestsCount(distributorRole?: SupportChatStateCountForm['distributorRole']) {
    this.getRequestsCount(distributorRole).subscribe();
  }

  private getRequestsCount(distributorRole?: SupportChatStateCountForm['distributorRole']) {
    // todo ask difference between prod-dist and client (Support and Requests)
    return this.adminSupportApiService.getCountForSupportChats({
        body: distributorRole
          ? { distributorRole }
          : {}
      },
      { disableErrorCatching: true, disableLoader: true }
    ).pipe(
      tap(({ result }) => {
        const newCounterValue = distributorRole === 'CLIENT_USER'
          ? result.unreadNew + result.unreadOpen
          : result.totalNew + result.unreadOpen;
        this.sidebarService.updateSupportCounter(result.unreadOpen);
        this.sidebarService.updateRequestsCounter(newCounterValue);
      }),
      catchError(() => null),
    );
  }

  forceUpdateProposalsCount() {
    this.getProposalsCount().subscribe();
  }

  private getProposalsCount() {
    if (this.appService.appMode$.getValue() === AppModes.CLIENT) {
      return of(0).pipe(
        tap((data) => {
          this.sidebarService.updateProposalsCounter(data);
        }),
        catchError(() => null),
      );
    }
    return this.adminProposalApiService
      .countNewProposals({}, { disableErrorCatching: true, disableLoader: true })
      .pipe(
        tap((data) => {
          this.sidebarService.updateProposalsCounter(data.result);
        }),
        catchError(() => null),
      );
  }

  forceUpdateNotificationCount() {
    this.getNotificationsCount().subscribe();
  }

  private getNotificationsCount() {
    if (this.appService.appMode$.getValue() === AppModes.PRODUCER) {
      return of(0).pipe(
        tap((data) => {
          this.sidebarService.updateNotificationCounter(data);
        })
      );
    }

    return this.adminNotificationsApiService
      .countUnreadNotifcationUser({}, { disableErrorCatching: true, disableLoader: true })
      .pipe(
        tap((data) => {
          this.sidebarService.updateNotificationCounter(data.result);
        }),
        catchError(() => null),
      );
  }

  updateCountersAfterAuth() {
    this.forceUpdateNotificationCount();
    this.forceUpdateProposalsCount();
    this.forceUpdateRequestsCount();
  }
}
